import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Image,
    HStack,
    Flex,
    Icon,
    IconButton,
    Progress,
    useColorModeValue,
    Button,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { FaUser, FaChevronLeft, FaChevronRight, FaCheck } from "react-icons/fa";
import { brandConfig } from "../brandConfig";
import { moduleData, ModuleContent, PageSection, ModulePage } from "../data/moduleData";

const ModuleTemplate = () => {
    const navigate = useNavigate();
    const { moduleId, pageNum } = useParams();
    const [currentPage, setCurrentPage] = useState(1);
    const cardBg = useColorModeValue("white", "gray.700");

    const parsedModuleId = parseInt(moduleId || "1");
    const validModuleIndex = Math.max(0, Math.min(parsedModuleId - 1, moduleData.length - 1));
    const module: ModuleContent = moduleData[validModuleIndex];
    const totalPages = module.pages.length;

    useEffect(() => {
        const parsedPage = parseInt(pageNum || "1");
        const validPage = Math.max(1, Math.min(parsedPage, totalPages));
        
        if (parsedPage !== validPage || parsedModuleId !== validModuleIndex + 1) {
            navigate(`/module/${validModuleIndex + 1}/${validPage}`, { replace: true });
        }
        
        setCurrentPage(validPage);
        window.scrollTo(0, 0);
    }, [pageNum, totalPages, parsedModuleId, validModuleIndex, navigate]);

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            navigate(`/module/${validModuleIndex + 1}/${page}`);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            goToPage(currentPage + 1);
        } else {
            navigate('/all-modules');
        }
    };

    const handlePrevious = () => {
        if (currentPage > 1) {
            goToPage(currentPage - 1);
        }
    };

    const currentPageData = module.pages[currentPage - 1] || module.pages[0];
    const progressPercentage = (currentPage / totalPages) * 100;

    return (
        <Box bg={brandConfig.colors.background.light} minHeight="100vh" position="relative">
            <Box 
                bg="white" 
                borderBottom="1px solid" 
                borderColor="gray.200"
                position="sticky"
                top={0}
                zIndex={100}
            >
                <Container maxW="7xl" px={{ base: 4, md: 6 }}>
                    <Flex 
                        justify="space-between" 
                        align="center" 
                        minH={{ base: "70px", md: "90px" }}
                        py={{ base: 1, md: 1 }}
                    >
                        <HStack spacing={2} cursor="pointer" onClick={() => navigate('/')}>
                            <Image 
                                src="/surgready/logo.png" 
                                alt="SurgReady" 
                                h={{ base: "60px", md: "80px" }}
                                objectFit="contain"
                            />
                        </HStack>
                        <HStack spacing={8}>
                            <Button
                                variant="ghost"
                                fontSize="lg"
                                fontWeight="semibold"
                                color={brandConfig.colors.text.primary}
                                onClick={() => navigate('/all-modules')}
                                _hover={{ bg: "gray.100" }}
                            >
                                All Modules
                            </Button>
                            <Button
                                variant="ghost"
                                fontSize="lg"
                                fontWeight="semibold"
                                color={brandConfig.colors.text.primary}
                                _hover={{ bg: "gray.100" }}
                            >
                                Community
                            </Button>
                            <HStack
                                spacing={3}
                                bg={brandConfig.colors.primary}
                                px={4}
                                py={2}
                                borderRadius="full"
                                cursor="pointer"
                                onClick={() => navigate('/member-profile')}
                                _hover={{ bg: brandConfig.colors.secondary }}
                                transition="all 0.2s"
                            >
                                <Text
                                    fontSize="lg"
                                    fontWeight="semibold"
                                    color="white"
                                >
                                    I am SurgReady
                                </Text>
                                <Box
                                    bg="white"
                                    borderRadius="full"
                                    w={10}
                                    h={10}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Icon as={FaUser} w={5} h={5} color={brandConfig.colors.primary} />
                                </Box>
                            </HStack>
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Box 
                bg={brandConfig.colors.primary} 
                py={3}
                position="sticky"
                top={{ base: "70px", md: "90px" }}
                zIndex={99}
            >
                <Container maxW="7xl" px={{ base: 4, md: 6 }}>
                    <Flex justify="space-between" align="center">
                        <HStack spacing={4}>
                            <Text color="white" fontWeight="bold" fontSize="lg">
                                Module {module.id}: {module.title}
                            </Text>
                        </HStack>
                        <HStack spacing={4}>
                            <Text color="white" fontSize="sm">
                                Page {currentPage} of {totalPages}
                            </Text>
                            <Box w="150px">
                                <Progress 
                                    value={progressPercentage} 
                                    size="sm" 
                                    colorScheme="whiteAlpha"
                                    bg="whiteAlpha.300"
                                    borderRadius="full"
                                />
                            </Box>
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Flex minH="calc(100vh - 180px)" position="relative">
                <IconButton
                    aria-label="Previous page"
                    icon={<Icon as={FaChevronLeft} w={8} h={8} />}
                    position="fixed"
                    left={{ base: 2, md: 8 }}
                    top="50%"
                    transform="translateY(-50%)"
                    size="lg"
                    w={{ base: 12, md: 16 }}
                    h={{ base: 12, md: 16 }}
                    borderRadius="full"
                    bg={currentPage > 1 ? brandConfig.colors.primary : "gray.300"}
                    color="white"
                    opacity={currentPage > 1 ? 1 : 0.5}
                    cursor={currentPage > 1 ? "pointer" : "not-allowed"}
                    onClick={handlePrevious}
                    _hover={{
                        bg: currentPage > 1 ? brandConfig.colors.secondary : "gray.300",
                        transform: currentPage > 1 ? "translateY(-50%) scale(1.1)" : "translateY(-50%)",
                    }}
                    transition="all 0.3s"
                    boxShadow="xl"
                    zIndex={50}
                />

                <IconButton
                    aria-label="Next page"
                    icon={currentPage === totalPages ? <Icon as={FaCheck} w={6} h={6} /> : <Icon as={FaChevronRight} w={8} h={8} />}
                    position="fixed"
                    right={{ base: 2, md: 8 }}
                    top="50%"
                    transform="translateY(-50%)"
                    size="lg"
                    w={{ base: 12, md: 16 }}
                    h={{ base: 12, md: 16 }}
                    borderRadius="full"
                    bg={brandConfig.colors.primary}
                    color="white"
                    onClick={handleNext}
                    _hover={{
                        bg: brandConfig.colors.secondary,
                        transform: "translateY(-50%) scale(1.1)",
                    }}
                    transition="all 0.3s"
                    boxShadow="xl"
                    zIndex={50}
                />

                <Container maxW="800px" py={{ base: 8, md: 12 }} px={{ base: 16, md: 8 }}>
                    <VStack spacing={8} align="stretch">
                        <Box 
                            bg={cardBg}
                            borderRadius="2xl"
                            boxShadow="xl"
                            overflow="hidden"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            {currentPageData.heroImage && (
                                <Box position="relative" h={{ base: "200px", md: "300px" }} overflow="hidden">
                                    <Image
                                        src={currentPageData.heroImage}
                                        alt={currentPageData.title}
                                        w="100%"
                                        h="100%"
                                        objectFit="cover"
                                    />
                                    <Box
                                        position="absolute"
                                        bottom={0}
                                        left={0}
                                        right={0}
                                        bg="linear-gradient(transparent, rgba(0,0,0,0.7))"
                                        p={6}
                                    >
                                        <Text color="white" fontSize="sm" fontWeight="medium" mb={1}>
                                            Page {currentPage} of {totalPages}
                                        </Text>
                                        <Heading color="white" size="xl">
                                            {currentPageData.title}
                                        </Heading>
                                    </Box>
                                </Box>
                            )}

                            <Box p={{ base: 6, md: 10 }}>
                                {!currentPageData.heroImage && (
                                    <Box mb={6}>
                                        <Text color={brandConfig.colors.primary} fontSize="sm" fontWeight="bold" mb={2}>
                                            Page {currentPage} of {totalPages}
                                        </Text>
                                        <Heading size="xl" color={brandConfig.colors.text.primary} mb={4}>
                                            {currentPageData.title}
                                        </Heading>
                                    </Box>
                                )}

                                <VStack spacing={6} align="stretch">
                                    {currentPageData.sections.map((section: PageSection, index: number) => (
                                        <Box key={index}>
                                            {section.heading && (
                                                <Heading 
                                                    as="h3" 
                                                    size="md" 
                                                    color={brandConfig.colors.secondary} 
                                                    mb={3}
                                                >
                                                    {section.heading}
                                                </Heading>
                                            )}
                                            <Text 
                                                fontSize="lg" 
                                                color={brandConfig.colors.text.primary} 
                                                lineHeight="1.8"
                                                whiteSpace="pre-line"
                                            >
                                                {section.content}
                                            </Text>
                                            {section.image && (
                                                <Box mt={4} borderRadius="lg" overflow="hidden">
                                                    <Image
                                                        src={section.image}
                                                        alt={section.heading || "Module content"}
                                                        w="100%"
                                                        borderRadius="lg"
                                                        boxShadow="md"
                                                    />
                                                    {section.imageCaption && (
                                                        <Text 
                                                            fontSize="sm" 
                                                            color={brandConfig.colors.text.muted}
                                                            fontStyle="italic"
                                                            textAlign="center"
                                                            mt={2}
                                                        >
                                                            {section.imageCaption}
                                                        </Text>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </VStack>

                                {currentPageData.keyPoints && currentPageData.keyPoints.length > 0 && (
                                    <Box 
                                        mt={8} 
                                        p={6} 
                                        bg={brandConfig.colors.background.surface}
                                        borderRadius="xl"
                                        borderLeft="4px solid"
                                        borderColor={brandConfig.colors.primary}
                                    >
                                        <Heading as="h4" size="sm" color={brandConfig.colors.primary} mb={4}>
                                            Key Takeaways
                                        </Heading>
                                        <VStack align="stretch" spacing={2}>
                                            {currentPageData.keyPoints.map((point: string, index: number) => (
                                                <HStack key={index} align="start" spacing={3}>
                                                    <Icon as={FaCheck} color={brandConfig.colors.status.success} mt={1} />
                                                    <Text color={brandConfig.colors.text.primary}>{point}</Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </VStack>
                </Container>
            </Flex>

            <Box 
                bg="white" 
                borderTop="1px solid" 
                borderColor="gray.200"
                py={4}
                position="sticky"
                bottom={0}
                zIndex={100}
            >
                <Container maxW="7xl" px={{ base: 4, md: 6 }}>
                    <Flex justify="space-between" align="center">
                        <Button
                            leftIcon={<Icon as={FaChevronLeft} />}
                            variant="outline"
                            colorScheme="gray"
                            onClick={handlePrevious}
                            isDisabled={currentPage <= 1}
                            size="lg"
                            borderRadius="full"
                        >
                            Previous
                        </Button>

                        <HStack spacing={2}>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <Box
                                    key={i}
                                    w={currentPage === i + 1 ? 8 : 3}
                                    h={3}
                                    borderRadius="full"
                                    bg={currentPage === i + 1 ? brandConfig.colors.primary : "gray.300"}
                                    cursor="pointer"
                                    onClick={() => goToPage(i + 1)}
                                    transition="all 0.3s"
                                    _hover={{ bg: brandConfig.colors.secondary }}
                                />
                            ))}
                        </HStack>

                        <Button
                            rightIcon={currentPage === totalPages ? <Icon as={FaCheck} /> : <Icon as={FaChevronRight} />}
                            bg={brandConfig.colors.primary}
                            color="white"
                            onClick={handleNext}
                            size="lg"
                            borderRadius="full"
                            _hover={{ bg: brandConfig.colors.secondary }}
                        >
                            {currentPage === totalPages ? "Complete Module" : "Next"}
                        </Button>
                    </Flex>
                </Container>
            </Box>
        </Box>
    );
};

export default ModuleTemplate;
